'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { createClient } from '@/lib/supabase/client'
import { Loader2, UserCircle2, LockKeyhole, ArrowRight, Store, Shield, WifiOff, KeyRound } from 'lucide-react'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { verifyOfflinePin, hasAnyOfflineSession, saveOfflineSession, getOfflineSession } from '@/lib/offline-session'
import { useOfflineSessionStore } from '@/store/offline-session.store'

interface LoginFormProps {
  loginType: 'pos' | 'admin'
  onLoginTypeChange: (type: 'pos' | 'admin') => void
}

export function LoginForm({ loginType, onLoginTypeChange }: LoginFormProps) {
  const [cedula, setCedula] = useState('')
  const [password, setPassword] = useState('')
  const [pin, setPin] = useState('')
  const [loading, setLoading] = useState(false)
  const [hasOfflineSessions, setHasOfflineSessions] = useState(false)

  // PIN setup after first login
  const [showPinSetup, setShowPinSetup] = useState(false)
  const [newPin, setNewPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [pendingSessionData, setPendingSessionData] = useState<Parameters<typeof saveOfflineSession>[2] | null>(null)

  const router = useRouter()
  const supabase = createClient()
  const isOnline = useOnlineStatus()
  const setOfflineSession = useOfflineSessionStore(s => s.setSession)

  useEffect(() => {
    hasAnyOfflineSession().then(setHasOfflineSessions).catch(() => {})
  }, [])

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search)
      if (sp.get('reason') === 'no-print') {
        toast.error('Equipo no autorizado', { duration: 6000 })
        const url = new URL(window.location.href)
        url.searchParams.delete('reason')
        window.history.replaceState({}, '', url.toString())
      }
    } catch {}
  }, [])

  const isOfflineMode = !isOnline && hasOfflineSessions

  // ─── Offline login with PIN ──────────────────
  async function handleOfflineLogin() {
    if (!cedula.trim() || !pin.trim()) return
    setLoading(true)
    try {
      const session = await verifyOfflinePin(cedula.trim(), pin)
      if (!session) {
        toast.error('Cédula o PIN incorrecto')
        setLoading(false)
        return
      }

      // Set offline session in Zustand store
      setOfflineSession(session)

      // Set login mode cookie
      document.cookie = `login-mode=pos; path=/; max-age=${60 * 60 * 24 * 30}; samesite=lax`

      // Navigate to POS (will be intercepted by offline POS wrapper)
      router.push('/pos')
    } catch {
      toast.error('Error al verificar credenciales offline')
    } finally {
      setLoading(false)
    }
  }

  // ─── Online login ────────────────────────────
  async function handleOnlineLogin() {
    setLoading(true)

    const email = `${cedula.trim()}@247stop.local`
    const { error } = await supabase.auth.signInWithPassword({ email, password })

    if (error) {
      toast.error('Cédula o contraseña incorrecta')
      setLoading(false)
      return
    }

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      toast.error('Error al obtener usuario')
      setLoading(false)
      return
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('role, must_change_password, active_store_id, org_id, full_name')
      .eq('id', user.id)
      .single()

    // Force password change if required
    if (profile?.must_change_password) {
      router.push('/change-password')
      router.refresh()
      return
    }

    // If POS login, cache session for offline use
    if (loginType === 'pos' && profile) {
      try {
        // Check if already has PIN
        const existing = await getOfflineSession(cedula.trim())
        if (existing) {
          // Silently update session data (keep same PIN)
          const { data: stores } = await supabase
            .from('stores').select('id, name').eq('org_id', profile.org_id).order('name')
          const { data: categories } = await supabase
            .from('categories').select('id, name, color').eq('org_id', profile.org_id).order('name')
          const { data: customers } = await supabase
            .from('customers')
            .select('id, name, document_type, document_number, email, phone, credit_enabled, credit_limit, credit_balance, customer_type')
            .eq('org_id', profile.org_id).order('name')
          const { data: voidSettings } = await supabase
            .from('void_settings')
            .select('lock_timeout_ms, always_require_auth, require_void_reason')
            .eq('org_id', profile.org_id).single()

          // Re-save with same PIN by re-hashing (we don't have the PIN, so use existing hash)
          // Actually we need to preserve the PIN — just save via the raw DB update
          await saveOfflineSessionDirect(cedula.trim(), existing.pinHash, existing.salt, {
            userId: user.id,
            orgId: profile.org_id,
            role: profile.role,
            fullName: profile.full_name,
            activeStoreId: profile.active_store_id ?? '',
            stores: stores ?? [],
            categories: categories ?? [],
            customers: customers ?? [],
            voidSettings: voidSettings ? {
              lockTimeoutMs: voidSettings.lock_timeout_ms,
              alwaysRequireAuth: voidSettings.always_require_auth,
              requireVoidReason: voidSettings.require_void_reason,
            } : null,
          })
        } else {
          // First time: fetch data and show PIN setup
          const { data: stores } = await supabase
            .from('stores').select('id, name').eq('org_id', profile.org_id).order('name')
          const { data: categories } = await supabase
            .from('categories').select('id, name, color').eq('org_id', profile.org_id).order('name')
          const { data: customers } = await supabase
            .from('customers')
            .select('id, name, document_type, document_number, email, phone, credit_enabled, credit_limit, credit_balance, customer_type')
            .eq('org_id', profile.org_id).order('name')
          const { data: voidSettings } = await supabase
            .from('void_settings')
            .select('lock_timeout_ms, always_require_auth, require_void_reason')
            .eq('org_id', profile.org_id).single()

          setPendingSessionData({
            userId: user.id,
            orgId: profile.org_id,
            role: profile.role,
            fullName: profile.full_name,
            activeStoreId: profile.active_store_id ?? '',
            stores: stores ?? [],
            categories: categories ?? [],
            customers: customers ?? [],
            voidSettings: voidSettings ? {
              lockTimeoutMs: voidSettings.lock_timeout_ms,
              alwaysRequireAuth: voidSettings.always_require_auth,
              requireVoidReason: voidSettings.require_void_reason,
            } : null,
          })
          setShowPinSetup(true)
          setLoading(false)
          return
        }
      } catch {
        // Non-critical: proceed without caching
      }
    }

    finishOnlineLogin(profile?.role ?? 'cashier')
  }

  function finishOnlineLogin(role: string) {
    document.cookie = `login-mode=${loginType}; path=/; max-age=${60 * 60 * 24 * 30}; samesite=lax`

    if (loginType === 'pos') {
      router.push('/pos')
    } else {
      const roleHome: Record<string, string> = {
        cashier: '/pos',
        manager: '/inventory',
        admin: '/reports',
      }
      router.push(roleHome[role] ?? '/pos')
    }
    router.refresh()
  }

  async function handlePinSave() {
    if (newPin.length < 4) {
      toast.error('El PIN debe tener al menos 4 dígitos')
      return
    }
    if (newPin !== confirmPin) {
      toast.error('Los PINs no coinciden')
      return
    }
    if (!pendingSessionData) return

    setLoading(true)
    try {
      await saveOfflineSession(cedula.trim(), newPin, pendingSessionData)
      toast.success('PIN configurado — podrás acceder sin conexión')
    } catch {
      toast.error('Error al guardar PIN')
    }
    setShowPinSetup(false)
    finishOnlineLogin(pendingSessionData.role)
  }

  function handleSkipPin() {
    if (pendingSessionData) {
      finishOnlineLogin(pendingSessionData.role)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isOfflineMode) {
      await handleOfflineLogin()
    } else {
      await handleOnlineLogin()
    }
  }

  // ─── PIN setup screen ───────────────────────
  if (showPinSetup) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <div className="h-12 w-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto">
            <KeyRound className="h-6 w-6 text-amber-600" />
          </div>
          <h2 className="text-lg font-bold">Configura un PIN offline</h2>
          <p className="text-sm text-muted-foreground">
            Este PIN te permitirá acceder al POS cuando no haya internet.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">PIN (mínimo 4 dígitos)</Label>
            <Input
              type="password" autoComplete="new-password" spellCheck={false} data-1p-ignore="true"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="••••"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              autoFocus
              className="h-12 text-center text-2xl tracking-[0.5em] font-bold"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Confirmar PIN</Label>
            <Input
              type="password" autoComplete="new-password" spellCheck={false} data-1p-ignore="true"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="••••"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handlePinSave()}
              className="h-12 text-center text-2xl tracking-[0.5em] font-bold"
            />
          </div>
        </div>

        <Button
          onClick={handlePinSave}
          disabled={loading || newPin.length < 4}
          className="w-full h-12 text-base font-bold rounded-xl bg-gradient-to-r from-[var(--brand-green)] to-[#208a4a] hover:from-[#208a4a] hover:to-[#1a703c] text-white"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Guardar PIN'}
        </Button>

        <button
          onClick={handleSkipPin}
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Omitir por ahora
        </button>
      </div>
    )
  }

  // ─── Main login form ────────────────────────
  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* Offline banner */}
      {isOfflineMode && (
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm font-semibold">
          <WifiOff className="h-4 w-4 flex-shrink-0" />
          <span>Sin conexión — ingresa con tu PIN offline</span>
        </div>
      )}

      {/* Login Type Selector — only show when online */}
      {!isOfflineMode && (
        <div className="flex flex-col sm:flex-row p-1 bg-muted/50 rounded-xl border border-muted-foreground/10 gap-1 sm:gap-0">
          <button
            type="button"
            onClick={() => onLoginTypeChange('pos')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 ${loginType === 'pos'
              ? 'bg-white dark:bg-zinc-800 text-[var(--brand-green)] shadow-sm ring-1 ring-black/5 dark:ring-white/5'
              : 'text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5'
              }`}
          >
            <Store className="h-4 w-4" />
            Punto de Venta
          </button>
          <button
            type="button"
            onClick={() => onLoginTypeChange('admin')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 ${loginType === 'admin'
              ? 'bg-white dark:bg-zinc-800 text-[var(--brand-orange)] shadow-sm ring-1 ring-black/5 dark:ring-white/5'
              : 'text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5'
              }`}
          >
            <Shield className="h-4 w-4" />
            Administración
          </button>
        </div>
      )}

      <div className="space-y-2 group">
        <Label htmlFor="cedula" className="text-sm font-semibold transition-colors group-focus-within:text-[var(--brand-green)]">
          Cédula de Identidad
        </Label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground group-focus-within:text-[var(--brand-green)] transition-colors">
            <UserCircle2 className="h-5 w-5" />
          </div>
          <Input
            id="cedula"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Número de cédula" autoComplete="off" spellCheck={false}
            value={cedula}
            onChange={(e) => setCedula(e.target.value.replace(/\D/g, ''))}
            required
            autoFocus
            className="pl-10 h-12 text-base transition-all bg-background/50 focus:bg-background border-muted-foreground/20 focus:border-[var(--brand-green)] focus:ring-[var(--brand-green)]/20 shadow-sm rounded-xl"
          />
        </div>
      </div>

      {isOfflineMode ? (
        /* PIN field for offline mode */
        <div className="space-y-2 group">
          <Label htmlFor="pin" className="text-sm font-semibold transition-colors group-focus-within:text-amber-500">
            PIN Offline
          </Label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground group-focus-within:text-amber-500 transition-colors">
              <KeyRound className="h-5 w-5" />
            </div>
            <Input
              id="pin"
              type="password" autoComplete="new-password" spellCheck={false} data-1p-ignore="true"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              required
              className="pl-10 h-12 text-base transition-all bg-background/50 focus:bg-background border-amber-200 focus:border-amber-500 focus:ring-amber-500/20 shadow-sm rounded-xl"
            />
          </div>
        </div>
      ) : (
        /* Password field for online mode */
        <div className="space-y-2 group">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-sm font-semibold transition-colors group-focus-within:text-[var(--brand-green)]">
              Contraseña
            </Label>
          </div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground group-focus-within:text-[var(--brand-green)] transition-colors">
              <LockKeyhole className="h-5 w-5" />
            </div>
            <Input
              id="password"
              type="password" autoComplete="new-password" spellCheck={false} data-1p-ignore="true"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="pl-10 h-12 text-base transition-all bg-background/50 focus:bg-background border-muted-foreground/20 focus:border-[var(--brand-green)] focus:ring-[var(--brand-green)]/20 shadow-sm rounded-xl"
            />
          </div>
        </div>
      )}

      <Button
        type="submit"
        className={`w-full h-12 text-base font-bold tracking-wide rounded-xl shadow-lg transition-all hover:-translate-y-0.5 active:translate-y-0 ${
          isOfflineMode
            ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-amber-500/20 hover:shadow-amber-500/40'
            : 'bg-gradient-to-r from-[var(--brand-green)] to-[#208a4a] hover:from-[#208a4a] hover:to-[#1a703c] text-white shadow-[var(--brand-green)]/20 hover:shadow-[var(--brand-green)]/40'
        }`}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {isOfflineMode ? 'Verificando...' : 'Ingresando...'}
          </>
        ) : (
          <>
            {isOfflineMode ? 'Acceder Offline' : 'Acceder al Sistema'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </>
        )}
      </Button>
    </form>
  )
}

// Helper to update session data without changing PIN
async function saveOfflineSessionDirect(
  cedula: string,
  pinHash: string,
  salt: string,
  data: Omit<import('@/lib/offline-session').OfflineSessionData, 'cedula' | 'pinHash' | 'salt' | 'cachedAt' | 'expiresAt'>
): Promise<void> {
  const now = new Date()
  const expires = new Date(now.getTime() + 72 * 60 * 60 * 1000)

  const session: import('@/lib/offline-session').OfflineSessionData = {
    cedula,
    pinHash,
    salt,
    ...data,
    cachedAt: now.toISOString(),
    expiresAt: expires.toISOString(),
  }

  const DB_NAME = 'ngi-pos-offline'
  const DB_VERSION = 2

  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result)
    request.onerror = (e) => reject((e.target as IDBOpenDBRequest).error)
  })

  return new Promise((resolve, reject) => {
    const tx = db.transaction('offline_sessions', 'readwrite')
    tx.objectStore('offline_sessions').put(session)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}
